import React, { memo, useMemo, useState } from 'react';
import {
  Typography,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Link,
  TextField,
  InputAdornment
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import VivproDatagrid from '../../Datagrid';
import CommonNameList from './CommonNameList';

import { regionSourceMapping, qcStatusList } from '../constants';
import styles from '../../RegulatoryInformation/styles/ApplicationTableView.styles';

const IngredientCuration = ({
  activeIngredient,
  allCommonNames,
  addNewCommonName,
  removeCommonName,
  handleInputChange,
  onValidate,
  handleActiveIngredientQcStatusChange,
  handleSubmit,
  activeIngredientTrials,
  currentValidatedActiveIngredient,
  setCurrentValidatedActiveIngredient
}: any) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const activeSubstanceKey = useMemo(() => {
    if (!activeIngredient) {
      return '';
    }

    return `${activeIngredient?.active_ingredient}-${activeIngredient?.source}`;
  }, [activeIngredient]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
      flex: 0.2,
      renderCell: (params: GridRenderCellParams<any>) => {
        const id = params?.row?.nct_id || params?.row?.euct_id || params?.row?.id || '';

        const source = regionSourceMapping[activeIngredient?.source?.toLowerCase() || ''];

        const url =
          source === 'us'
            ? `https://www.ria.vivpro.ai/regulatory360/core/${id}/ct`
            : `https://www.clinicaltrialsregister.eu/ctr-search/search?query=${id}`;

        return (
          <Link href={url} target='_blank' rel='noopener'>
            {id}
          </Link>
        );
      }
    },
    {
      field: 'study_title',
      headerName: 'Study Title',
      width: 100,
      flex: 1
    }
  ];

  const filteredTrials = useMemo(() => {
    const trials = activeIngredientTrials[currentValidatedActiveIngredient] || [];

    const sanitizedTrials = trials.map((trial: any) => {
      return {
        ...trial,
        id: trial?.nct_id || trial?.euct_id || trial?.id || '',
        study_title: trial?.study_title || ''
      };
    });

    setIsLoading(false);

    if (searchValue.trim().length > 0) {
      return sanitizedTrials.filter((trial: any) => {
        const trialTitle = trial?.study_title?.toLowerCase() || '';
        const id = trial?.id?.toLowerCase() || '';
        return (
          trialTitle.includes(searchValue.toLowerCase()) || id.includes(searchValue.toLowerCase())
        );
      });
    }

    return sanitizedTrials;
  }, [activeIngredient, activeIngredientTrials, searchValue]);

  return (
    <Stack direction='row' mt={2} justifyContent='space-between' gap={2} height='72vh'>
      <Stack gap={2} alignItems='flex-start'>
        <Stack spacing={1}>
          <Stack direction='row' gap={1}>
            <Typography fontWeight='bold' fontSize={18}>
              Active Substance:
            </Typography>
            <Typography fontSize={18}>
              {activeIngredient?.active_ingredient?.toUpperCase()} (
              {activeIngredient?.source?.toUpperCase()})
            </Typography>
          </Stack>
          <Stack direction='row' gap={1}>
            <Typography fontSize={18} fontWeight='bold'>
              Base Trials:
            </Typography>
            <Typography fontSize={18}>{activeIngredient?.base_trials_count}</Typography>
          </Stack>
        </Stack>
        <Stack gap={2} alignItems='center' key={activeSubstanceKey}>
          <CommonNameList
            commonNames={allCommonNames[activeSubstanceKey] || []}
            addNewCommonName={addNewCommonName}
            removeCommonName={removeCommonName}
            handleInputChange={handleInputChange}
            mainKey={`${activeIngredient?.active_ingredient}-${activeIngredient?.source}`}
            type='Base'
            onValidate={(index: number, mainKey: string, title: string, type: string) => {
              setIsLoading(true);
              setCurrentValidatedActiveIngredient(title);
              onValidate(index, mainKey, title, type);
            }}
          />
          <Stack gap={2}>
            <FormControl size='small' sx={{ width: 250 }}>
              <InputLabel>QC Status</InputLabel>
              <Select
                value={activeIngredient?.active_ingredient_qc_status}
                label='QC Status'
                onChange={handleActiveIngredientQcStatusChange}>
                {qcStatusList.map(qcStatus => (
                  <MenuItem key={qcStatus} value={qcStatus}>
                    {qcStatus}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant='contained'
              size='small'
              onClick={event => handleSubmit(event, activeSubstanceKey, 'active_ingredient')}>
              Submit
            </Button>
          </Stack>
        </Stack>
      </Stack>
      {filteredTrials?.length > 0 && !isLoading && (
        <Stack>
          <TextField
            variant='outlined'
            type='search'
            id='search'
            label='Search'
            value={searchValue}
            InputProps={{
              style: { fontSize: '1.5vh', minWidth: '250px', maxWidth: '450px' },
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              )
            }}
            InputLabelProps={{
              style: { fontSize: '1.5vh' }
            }}
            onChange={e => {
              setSearchValue(e.target.value);
            }}
          />
          <Box height='70vh' minWidth='62vw' mt={2} pr={3.75} flex={1} sx={styles.root}>
            <VivproDatagrid
              rows={filteredTrials}
              columnsMapping={columns}
              rowId='id'
              loading={false}
              autoRowHeight
            />
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(IngredientCuration);
